import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Minus } from "lucide-react";
import Counter from "@/components/landing/Counter";
import Reveal from "@/components/landing/Reveal";

const APP_URL = "https://verdictsystem.com/app";

const Nav = () => (
    <header
        className="sticky top-0 z-40 border-b border-[#1f1f1f] bg-[#050505]/80 backdrop-blur-md"
        data-testid="site-nav"
    >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 sm:px-8">
            <a
                href="/"
                className="group flex items-baseline gap-2"
                data-testid="nav-logo"
            >
                <span className="font-display text-2xl font-black tracking-tighter">
                    Verdict
                </span>
                <span className="hidden text-[10px] uppercase tracking-[0.25em] text-[#71717a] sm:inline">
                    Clarity over comfort
                </span>
            </a>
            <a
                href={APP_URL}
                className="group inline-flex items-center gap-2 border border-white/20 bg-transparent px-4 py-2 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:border-white hover:bg-white hover:text-black"
                data-testid="nav-cta"
            >
                Get Your Verdict
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
        </div>
    </header>
);

const TickerStrip = () => {
    const items = [
        "They text less but stay \"busy\"",
        "Plans fade. Nothing is clearly wrong.",
        "Replies get shorter",
        "You overthink every word",
        "Effort drops. Excuses rise.",
        "You feel it. You can't prove it.",
    ];
    const repeated = [...items, ...items];
    return (
        <div
            className="overflow-hidden border-y border-[#1f1f1f] bg-[#0a0a0a] py-3"
            data-testid="ticker-strip"
        >
            <div className="marquee-track flex w-max gap-12 whitespace-nowrap">
                {repeated.map((t, i) => (
                    <span
                        key={i}
                        className="flex items-center gap-4 text-xs uppercase tracking-[0.3em] text-[#71717a]"
                    >
                        <Minus className="h-3 w-3 text-[#ff3b30]" />
                        {t}
                    </span>
                ))}
            </div>
        </div>
    );
};

const Hero = () => {
    return (
        <section className="relative overflow-hidden" data-testid="hero">
            {/* Background grid lines */}
            <div className="pointer-events-none absolute inset-0 opacity-[0.06]">
                <div className="h-full w-full"
                    style={{
                        backgroundImage:
                            "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                        backgroundSize: "80px 80px",
                    }}
                />
            </div>

            <div className="relative mx-auto grid max-w-7xl grid-cols-12 gap-6 px-6 pb-24 pt-20 sm:px-8 sm:pb-32 sm:pt-28">
                {/* Left meta */}
                <div className="col-span-12 lg:col-span-3">
                    <Reveal>
                        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
                            <span className="h-1.5 w-1.5 bg-[#ff3b30]"></span>
                            Live · Reading Reality
                        </div>
                        <div className="mt-6 hidden h-px w-16 bg-[#2a2a2a] lg:block" />
                        <p className="mt-6 hidden font-mono text-[11px] uppercase tracking-widest text-[#71717a] lg:block">
                            01 / Hero
                        </p>
                    </Reveal>
                </div>

                {/* Headline */}
                <div className="col-span-12 lg:col-span-9">
                    <Reveal>
                        <h1 className="font-display text-[44px] font-black leading-[0.95] tracking-tighter sm:text-7xl lg:text-[112px]">
                            Stop Guessing.
                            <br />
                            <span className="text-[#a1a1aa]">Start </span>
                            <span className="relative inline-block">
                                Knowing.
                                <span className="absolute -bottom-2 left-0 h-[6px] w-full bg-[#ff3b30]" />
                            </span>
                        </h1>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <p className="mt-10 max-w-2xl font-body text-lg leading-relaxed text-[#a1a1aa] sm:text-xl">
                            Understand your situation, see their real interest, and track how
                            you're actually handling it —
                            <span className="text-white"> with brutal clarity and real guidance.</span>
                        </p>
                    </Reveal>

                    <Reveal delay={0.18}>
                        <div className="mt-12 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                            <a
                                href={APP_URL}
                                className="group inline-flex items-center gap-3 bg-white px-8 py-5 font-display text-base font-bold uppercase tracking-wider text-black transition-colors hover:bg-[#ff3b30] hover:text-white"
                                data-testid="hero-primary-cta"
                            >
                                Get Your Verdict
                                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                            </a>
                            <a
                                href={APP_URL}
                                className="group inline-flex items-center gap-3 border border-white/25 bg-transparent px-8 py-5 font-display text-base font-bold uppercase tracking-wider text-white transition-colors hover:border-white hover:bg-white/5"
                                data-testid="hero-secondary-cta"
                            >
                                Try It Free — No Signup
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.26}>
                        <div className="mt-6 flex flex-col gap-1">
                            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717a]">
                                Takes 30 seconds. No signup.
                            </p>
                            <p className="text-sm text-[#a1a1aa]" data-testid="hero-trust-line">
                                No fluff. No false hope. Just reality + direction.
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

const Problem = () => {
    const bullets = [
        {
            tag: "01",
            line: "They text less... but say they're \"busy.\"",
        },
        {
            tag: "02",
            line: "Plans fade... but nothing is clearly \"wrong.\"",
        },
        {
            tag: "03",
            line: "You overthink every message.",
        },
    ];
    return (
        <section
            className="relative border-t border-[#1f1f1f] py-24 sm:py-32"
            data-testid="problem-section"
        >
            <div className="mx-auto grid max-w-7xl grid-cols-12 gap-6 px-6 sm:px-8">
                <div className="col-span-12 lg:col-span-4">
                    <Reveal>
                        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                            02 / The Loop
                        </p>
                        <h2 className="mt-6 font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-5xl">
                            You're not
                            <br />
                            confused.
                        </h2>
                        <h2 className="mt-3 font-display text-4xl font-black leading-[0.95] tracking-tighter text-[#a1a1aa] sm:text-5xl">
                            You're missing
                            <br />
                            <span className="text-white">clear feedback.</span>
                        </h2>
                    </Reveal>
                </div>

                <div className="col-span-12 lg:col-span-8 lg:pl-12">
                    <div className="space-y-0">
                        {bullets.map((b, i) => (
                            <Reveal key={b.tag} delay={i * 0.08}>
                                <div className="flex items-start gap-6 border-b border-[#1f1f1f] py-6">
                                    <span className="font-mono text-xs text-[#71717a]">
                                        {b.tag}
                                    </span>
                                    <p className="font-display text-xl font-medium leading-snug tracking-tight text-white sm:text-2xl">
                                        {b.line}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.3}>
                        <div className="mt-10 flex flex-col gap-2">
                            <p className="text-[#a1a1aa]">So you try to fix it.</p>
                            <p className="text-[#a1a1aa]">
                                And somehow… you end up feeling worse.
                            </p>
                        </div>
                    </Reveal>

                    <Reveal delay={0.4}>
                        <div className="mt-10 inline-flex items-center gap-3 border border-[#2a2a2a] bg-[#0a0a0a] px-5 py-3">
                            <span className="h-2 w-2 bg-[#ff3b30]"></span>
                            <p className="font-display text-sm font-bold uppercase tracking-wider">
                                Most tools give you comfort. <span className="text-[#ff3b30]">This gives you clarity.</span>
                            </p>
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};

const Pillar = ({ index, title, desc, accent }) => (
    <Reveal delay={index * 0.08}>
        <div
            className="group relative flex h-full flex-col justify-between border border-[#1f1f1f] bg-[#0a0a0a] p-8 transition-colors hover:border-[#3a3a3a]"
            data-testid={`pillar-${index}`}
        >
            <div>
                <div className="flex items-center justify-between">
                    <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                        Pillar / 0{index + 1}
                    </span>
                    <span className={`h-2 w-2 ${accent}`}></span>
                </div>
                <h3 className="mt-10 font-display text-2xl font-black uppercase leading-tight tracking-tighter sm:text-3xl">
                    {title}
                </h3>
                <p className="mt-4 text-[15px] leading-relaxed text-[#a1a1aa]">
                    {desc}
                </p>
            </div>
            <div className="mt-12 flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#71717a] transition-colors group-hover:text-white">
                Built into every read
                <ArrowRight className="h-3 w-3" />
            </div>
        </div>
    </Reveal>
);

const SystemSection = () => {
    const pillars = [
        {
            title: "Partner's Interest",
            desc: "See what their behavior actually shows — not what you hope it means.",
            accent: "bg-[#ff3b30]",
        },
        {
            title: "Your Growth",
            desc: "Track how you're handling the situation — and level up through better decisions.",
            accent: "bg-[#34c759]",
        },
        {
            title: "Guidance",
            desc: "Clear next steps based on reality, not emotion.",
            accent: "bg-white",
        },
    ];
    return (
        <section
            className="relative border-t border-[#1f1f1f] py-24 sm:py-32"
            data-testid="system-section"
        >
            <div className="mx-auto max-w-7xl px-6 sm:px-8">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 lg:col-span-7">
                        <Reveal>
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                                03 / The System
                            </p>
                            <h2 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl lg:text-7xl">
                                This isn't advice.
                                <br />
                                <span className="text-[#a1a1aa]">It's a </span>
                                <span className="relative inline-block">
                                    system.
                                    <span className="absolute -bottom-2 left-0 h-[6px] w-full bg-white" />
                                </span>
                            </h2>
                        </Reveal>
                    </div>
                    <div className="col-span-12 lg:col-span-5 lg:pt-12">
                        <Reveal delay={0.1}>
                            <p className="text-lg leading-relaxed text-[#a1a1aa]">
                                Every situation is broken down into three brutal reads.
                                No guessing. No chasing illusions. Just truth —
                                <span className="text-white"> and a way forward.</span>
                            </p>
                        </Reveal>
                    </div>
                </div>

                <div className="mt-16 grid grid-cols-1 gap-0 border border-[#1f1f1f] md:grid-cols-3">
                    {pillars.map((p, i) => (
                        <div
                            key={p.title}
                            className={`${i < pillars.length - 1 ? "md:border-r" : ""} border-[#1f1f1f] ${i > 0 ? "border-t md:border-t-0" : ""}`}
                        >
                            <Pillar index={i} title={p.title} desc={p.desc} accent={p.accent} />
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};

const LiveExample = () => {
    return (
        <section
            className="relative border-t border-[#1f1f1f] bg-[#070707] py-24 sm:py-32"
            data-testid="live-example-section"
        >
            <div className="mx-auto max-w-7xl px-6 sm:px-8">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 lg:col-span-5">
                        <Reveal>
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                                04 / Live Example
                            </p>
                            <h2 className="mt-6 font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-6xl">
                                This is what
                                <br />
                                <span className="text-[#a1a1aa]">clarity</span>
                                <br />
                                looks like.
                            </h2>
                            <p className="mt-8 max-w-md text-[#a1a1aa]">
                                A real verdict. Two scores. One direction. No spin.
                            </p>
                        </Reveal>
                    </div>

                    {/* Mock dashboard card */}
                    <div className="col-span-12 lg:col-span-7">
                        <Reveal delay={0.1}>
                            <div
                                className="border border-[#2a2a2a] bg-[#0a0a0a]"
                                data-testid="verdict-card"
                            >
                                {/* Header bar */}
                                <div className="flex items-center justify-between border-b border-[#1f1f1f] px-6 py-4">
                                    <div className="flex items-center gap-3">
                                        <div className="flex gap-1.5">
                                            <span className="h-2 w-2 bg-[#ff3b30]" />
                                            <span className="h-2 w-2 bg-[#71717a]" />
                                            <span className="h-2 w-2 bg-[#34c759]" />
                                        </div>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717a]">
                                            Verdict / Case #4271
                                        </span>
                                    </div>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717a]">
                                        Generated · 0.4s
                                    </span>
                                </div>

                                {/* Scores grid */}
                                <div className="grid grid-cols-1 md:grid-cols-2">
                                    {/* Partner's Interest */}
                                    <div className="relative border-b border-[#1f1f1f] p-8 md:border-b-0 md:border-r">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
                                                Partner's Interest
                                            </span>
                                            <span className="inline-flex items-center gap-1.5 border border-[#ff3b30]/40 bg-[#ff3b30]/10 px-2 py-0.5">
                                                <span className="h-1.5 w-1.5 bg-[#ff3b30]" />
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-[#ff3b30]">
                                                    Fading
                                                </span>
                                            </span>
                                        </div>
                                        <div className="mt-4 flex items-baseline gap-3">
                                            <Counter
                                                to={28}
                                                className="font-mono text-7xl font-bold leading-none text-[#ff3b30] sm:text-8xl"
                                                testId="partner-interest-score"
                                            />
                                            <span className="font-mono text-2xl text-[#3a3a3a]">/100</span>
                                        </div>
                                        {/* Bar */}
                                        <div className="mt-6 h-1 w-full bg-[#1f1f1f]">
                                            <motion.div
                                                className="h-full bg-[#ff3b30]"
                                                initial={{ width: 0 }}
                                                whileInView={{ width: "28%" }}
                                                viewport={{ once: true, margin: "-20% 0px" }}
                                                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                                            />
                                        </div>
                                        <p className="mt-6 text-sm leading-relaxed text-[#a1a1aa]">
                                            Reduced effort and consistency over time.
                                        </p>
                                    </div>

                                    {/* Your Growth */}
                                    <div className="relative p-8">
                                        <div className="flex items-center justify-between">
                                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
                                                Your Growth
                                            </span>
                                            <span className="inline-flex items-center gap-1.5 border border-[#34c759]/40 bg-[#34c759]/10 px-2 py-0.5">
                                                <span className="h-1.5 w-1.5 bg-[#34c759]" />
                                                <span className="font-mono text-[10px] uppercase tracking-widest text-[#34c759]">
                                                    Moving Forward
                                                </span>
                                            </span>
                                        </div>
                                        <div className="mt-4 flex items-baseline gap-3">
                                            <Counter
                                                to={64}
                                                className="font-mono text-7xl font-bold leading-none text-[#34c759] sm:text-8xl"
                                                testId="your-growth-score"
                                            />
                                            <span className="font-mono text-2xl text-[#3a3a3a]">/100</span>
                                        </div>
                                        <div className="mt-6 h-1 w-full bg-[#1f1f1f]">
                                            <motion.div
                                                className="h-full bg-[#34c759]"
                                                initial={{ width: 0 }}
                                                whileInView={{ width: "64%" }}
                                                viewport={{ once: true, margin: "-20% 0px" }}
                                                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                                            />
                                        </div>
                                        <p className="mt-6 text-sm leading-relaxed text-[#a1a1aa]">
                                            You've taken control instead of chasing.
                                        </p>
                                    </div>
                                </div>

                                {/* Guidance */}
                                <div className="border-t border-[#1f1f1f] bg-[#080808] p-8">
                                    <div className="flex items-center justify-between">
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
                                            Guidance
                                        </span>
                                        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717a]">
                                            Next 7 days
                                        </span>
                                    </div>
                                    <p
                                        className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight sm:text-3xl"
                                        data-testid="guidance-text"
                                    >
                                        Pull back. Stop initiating.
                                        <br />
                                        <span className="text-[#a1a1aa]">Let their actions reveal intent.</span>
                                    </p>
                                </div>
                            </div>
                        </Reveal>

                        <Reveal delay={0.3}>
                            <div className="mt-8 flex items-center gap-3">
                                <a
                                    href={APP_URL}
                                    className="group inline-flex items-center gap-3 bg-white px-6 py-4 font-display text-sm font-bold uppercase tracking-wider text-black transition-colors hover:bg-[#ff3b30] hover:text-white"
                                    data-testid="example-cta"
                                >
                                    Run Yours Now
                                    <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                                </a>
                                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#71717a]">
                                    No signup · 30 sec
                                </p>
                            </div>
                        </Reveal>
                    </div>
                </div>
            </div>
        </section>
    );
};

const FounderStory = () => {
    return (
        <section
            className="relative border-t border-[#1f1f1f] py-24 sm:py-32"
            data-testid="founder-section"
        >
            <div className="mx-auto max-w-7xl px-6 sm:px-8">
                <div className="grid grid-cols-12 gap-8 lg:gap-12">
                    {/* Photo */}
                    <div className="col-span-12 md:col-span-5">
                        <Reveal>
                            <div className="relative bg-black">
                                <img
                                    src="https://customer-assets.emergentagent.com/job_knowing-now/artifacts/zzvzpxvo_Jimmy.jpg"
                                    alt="Jimmy — Founder of Verdict"
                                    className="aspect-[4/5] w-full object-cover grayscale contrast-125 brightness-90"
                                    style={{ objectPosition: "50% 30%" }}
                                    data-testid="founder-photo"
                                />
                                {/* Vignette: fade bright sky into the dark UI */}
                                <div
                                    className="pointer-events-none absolute inset-0"
                                    style={{
                                        background:
                                            "linear-gradient(180deg, rgba(5,5,5,0.85) 0%, rgba(5,5,5,0.35) 30%, rgba(5,5,5,0.05) 55%, rgba(5,5,5,0.6) 100%)",
                                    }}
                                />
                                <div
                                    className="pointer-events-none absolute inset-0"
                                    style={{
                                        background:
                                            "radial-gradient(ellipse at 50% 60%, rgba(5,5,5,0) 35%, rgba(5,5,5,0.55) 90%)",
                                    }}
                                />
                                <div className="absolute -bottom-3 left-0 z-10 flex items-center gap-3 bg-[#050505] px-4 py-2">
                                    <span className="h-2 w-2 bg-[#ff3b30]" />
                                    <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#a1a1aa]">
                                        Jimmy · Founder of Verdict
                                    </span>
                                </div>
                            </div>
                        </Reveal>
                    </div>

                    {/* Story */}
                    <div className="col-span-12 md:col-span-7 md:pl-4">
                        <Reveal>
                            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                                05 / Why this exists
                            </p>
                            <h2 className="mt-6 font-display text-4xl font-black leading-[0.95] tracking-tighter sm:text-5xl">
                                I didn't build this
                                <br />
                                because I <span className="italic text-[#a1a1aa]">wanted</span> to.
                                <br />
                                I built it because
                                <br />
                                I <span className="border-b-4 border-[#ff3b30] pb-1">had</span> to.
                            </h2>
                        </Reveal>

                        <div className="mt-10 space-y-5 text-lg leading-relaxed text-[#a1a1aa]">
                            <Reveal delay={0.1}>
                                <p>
                                    After 15 years, our relationship was dying — and I was left trying to understand why.
                                </p>
                            </Reveal>
                            <Reveal delay={0.15}>
                                <p>
                                    I was desperate to understand what went wrong — but all I found were apps that sold me <span className="line-through decoration-[#ff3b30]">hope</span> instead of <span className="text-white">truth</span>.
                                </p>
                            </Reveal>
                            <Reveal delay={0.2}>
                                <p className="text-white">
                                    So I built something that wouldn't lie to me.
                                </p>
                            </Reveal>
                            <Reveal delay={0.25}>
                                <p>
                                    Something that looks at <span className="text-white">behavior</span>, not words.
                                    Something that forces clarity.
                                </p>
                            </Reveal>
                            <Reveal delay={0.3}>
                                <p className="border-l-2 border-[#ff3b30] pl-4 text-white">
                                    Now I'm releasing it for anyone who needs that same truth.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const FinalCTA = () => (
    <section
        className="relative overflow-hidden border-t border-[#1f1f1f] bg-[#080808] py-32 sm:py-40"
        data-testid="final-cta-section"
    >
        <div className="pointer-events-none absolute inset-0 opacity-[0.05]">
            <div
                className="h-full w-full"
                style={{
                    backgroundImage:
                        "linear-gradient(to right, #fff 1px, transparent 1px), linear-gradient(to bottom, #fff 1px, transparent 1px)",
                    backgroundSize: "60px 60px",
                }}
            />
        </div>

        <div className="relative mx-auto max-w-7xl px-6 sm:px-8">
            <Reveal>
                <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#71717a]">
                    06 / The Verdict
                </p>
            </Reveal>
            <Reveal delay={0.05}>
                <h2 className="mt-6 max-w-5xl font-display text-5xl font-black leading-[0.95] tracking-tighter sm:text-7xl lg:text-[96px]">
                    You already know
                    <br />
                    something feels off.
                </h2>
            </Reveal>
            <Reveal delay={0.15}>
                <p className="mt-10 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight text-[#a1a1aa] sm:text-4xl">
                    Do you want <span className="text-white">comfort</span>...
                    <br />
                    or the <span className="text-[#ff3b30]">truth</span>?
                </p>
            </Reveal>

            <Reveal delay={0.25}>
                <div className="mt-14 flex flex-col items-start gap-6 sm:flex-row sm:items-center">
                    <a
                        href={APP_URL}
                        className="group inline-flex items-center gap-3 bg-[#ff3b30] px-10 py-6 font-display text-lg font-black uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-black"
                        data-testid="final-primary-cta"
                    >
                        Get Your Verdict
                        <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </a>
                    <div className="flex flex-col gap-1">
                        <p className="font-mono text-xs uppercase tracking-[0.25em] text-white">
                            Takes 30 seconds. No signup.
                        </p>
                        <p className="text-sm text-[#a1a1aa]">
                            Start understanding. Start improving. Stop guessing.
                        </p>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

const Footer = () => (
    <footer
        className="border-t border-[#1f1f1f] py-10"
        data-testid="site-footer"
    >
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-4 px-6 sm:flex-row sm:items-center sm:px-8">
            <div className="flex items-baseline gap-3">
                <span className="font-display text-xl font-black tracking-tighter">
                    Verdict
                </span>
                <span className="text-[10px] uppercase tracking-[0.25em] text-[#71717a]">
                    Clarity over comfort
                </span>
            </div>
            <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#71717a]">
                © {new Date().getFullYear()} Verdict · No fluff. No false hope.
            </p>
        </div>
    </footer>
);

export default function Landing() {
    return (
        <div className="relative">
            <Nav />
            <main>
                <Hero />
                <TickerStrip />
                <Problem />
                <SystemSection />
                <LiveExample />
                <FounderStory />
                <FinalCTA />
            </main>
            <Footer />
        </div>
    );
}
